import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Settings } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { toast } from '@/components/ui/toaster';

/** Partial `PATCH /settings` body: top-level keys are replaced whole (nested objects included). */
export type SettingsPatch = Partial<Settings>;

/** `GET /settings` (readable by every role). */
export function useSettingsQuery() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => api.get<Settings>('/settings'),
  });
}

/** `PATCH /settings` (admin). Puts the returned settings in the cache and toasts. */
export function useUpdateSettings(successMessage = 'Settings saved') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: SettingsPatch) => api.patch<Settings>('/settings', patch),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.settings, data);
      toast.success(successMessage);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Tolerates both `[...]` and `{ items: [...] }` list responses. */
export function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}
